
import { useState, useEffect, useRef } from "react";
import { AccountPerformanceData } from "@/components/marketplace/types/strategy-card-types";
import { supabase } from "@/integrations/supabase/client";

export const useAccountPerformance = (strategies: any[], isLoading: boolean) => {
  const [accountPerformance, setAccountPerformance] = useState<Record<string, AccountPerformanceData>>({});
  const [performanceLoading, setPerformanceLoading] = useState(true);
  const cachedPerformanceRef = useRef<Record<string, AccountPerformanceData>>({});
  const lastFetchTimeRef = useRef<number>(0);
  const CACHE_TTL = 5 * 60 * 1000; // 5 minutes cache

  useEffect(() => {
    const fetchAccountPerformance = async () => {
      setPerformanceLoading(true);
      try {
        // Skip fetching if we have no strategies
        if (strategies.length === 0) {
          setPerformanceLoading(false);
          return;
        }

        // Use strategy_id rather than id if available
        const strategyIds = strategies.map(s => s.strategy_id || s.id).filter(Boolean);
        
        // Check if the cache is still valid
        const now = Date.now();
        if (now - lastFetchTimeRef.current < CACHE_TTL && Object.keys(cachedPerformanceRef.current).length > 0) {
          // Filter the cached data to only include strategies we're currently looking at
          const filteredCache: Record<string, AccountPerformanceData> = {};
          strategyIds.forEach(id => {
            if (cachedPerformanceRef.current[id]) {
              filteredCache[id] = cachedPerformanceRef.current[id];
            }
          });
          
          // Check if we have cache hits for all requested strategies
          const allStrategiesInCache = strategyIds.every(id => filteredCache[id]);
          
          if (allStrategiesInCache) {
            setAccountPerformance(filteredCache);
            setPerformanceLoading(false);
            return;
          }
        }

        // Batch fetch the primary accounts for all strategies at once
        const { data: accountsData, error: accountsError } = await supabase
          .from('account')
          .select('id, strategy_id')
          .in('strategy_id', strategyIds)
          .eq('is_primary', true);

        if (accountsError) {
          console.error('Error fetching primary accounts:', accountsError);
          setPerformanceLoading(false);
          return;
        }

        if (!accountsData || accountsData.length === 0) {
          setPerformanceLoading(false);
          return;
        }

        // Build a map of strategy_id to account_id
        const strategyToAccountMap: Record<string, string> = {};
        accountsData.forEach(account => {
          strategyToAccountMap[account.strategy_id] = account.id;
        });

        // Get all account IDs and fetch performance data in a single query
        const accountIds = accountsData.map(a => a.id);
        const { data: performanceData, error: performanceError } = await supabase
          .from('account_performance')
          .select('*')
          .in('account_id', accountIds)
          .order('timestamp', { ascending: false });

        if (performanceError) {
          console.error('Error fetching account performance:', performanceError);
          setPerformanceLoading(false);
          return;
        }

        // Group performance entries by account_id and find the latest for each
        const latestPerformanceByAccount: Record<string, AccountPerformanceData> = {};
        
        if (performanceData && Array.isArray(performanceData)) {
          // Build a map of account_id to latest performance entry
          performanceData.forEach(perf => {
            const accountId = perf.account_id;
            if (!latestPerformanceByAccount[accountId] || 
                new Date(perf.timestamp) > new Date(latestPerformanceByAccount[accountId].timestamp)) {
              latestPerformanceByAccount[accountId] = perf as AccountPerformanceData;
            }
          });
        }
        
        // Map the latest performance entries back to strategies
        const latestPerformanceByStrategy: Record<string, AccountPerformanceData> = {};
        Object.entries(strategyToAccountMap).forEach(([strategyId, accountId]) => {
          if (latestPerformanceByAccount[accountId]) {
            latestPerformanceByStrategy[strategyId] = latestPerformanceByAccount[accountId];
          }
        });

        // Update cache
        cachedPerformanceRef.current = {
          ...cachedPerformanceRef.current,
          ...latestPerformanceByStrategy
        };
        lastFetchTimeRef.current = now;
        
        setAccountPerformance(latestPerformanceByStrategy);
      } catch (err) {
        console.error('Unexpected error fetching account performance:', err);
      } finally {
        setPerformanceLoading(false);
      }
    };

    if (!isLoading && strategies.length > 0) {
      fetchAccountPerformance();
    } else {
      setPerformanceLoading(false);
    }
  }, [strategies, isLoading]);

  return { accountPerformance, performanceLoading };
};
